import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserDetails, sendOtp, verifyOtp, ResetPasswordWithOtp } from '../../api/api';
import { startSessionLog } from '../../utils/session';
import { isDemoMobile } from '../../utils/demo';
import { tenDigits } from '../../utils/helpers';
import {
  verifyDevice, bindThisDevice, loadOrganisers, passwordProblem, humanReason, thisDeviceId,
} from '../../utils/authFlow';

// "Forgot password" for an account that already exists (self-registered, or added
// by an admin). Three steps:
//   mobile   → the number must belong to an active Evt_Users row → WhatsApp OTP
//   otp      → UpdateOtpVerification
//   password → ResetPasswordWithOtp (the API re-checks the verified OTP), then sign
//              in on THIS device. The OTP already proves the phone, so an account
//              bound elsewhere is moved here without asking for a second code.
// Meta bills each OTP: within an hour the API re-uses the earlier code (`reused`).
export function useForgotPassword() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { state } = useLocation();
  const [step, setStep] = useState('mobile');
  const [mobile, setMobileRaw] = useState(() => String(state?.mobile || '').replace(/\D/g, '').slice(0, 10));
  const [otp, setOtpRaw] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [reused, setReused] = useState(null);

  const setMobile = (v) => setMobileRaw(tenDigits(v)); // last 10 digits (autofill gives +91 …)
  const setOtp = (v) => setOtpRaw(String(v || '').replace(/\D/g, '').slice(0, 4));

  const run = async (fn, fallback) => {
    setErr(''); setBusy(true);
    try { await fn(); } catch { setErr(fallback); } finally { setBusy(false); }
  };

  const requestOtp = async () => {
    const r = await sendOtp({ MobileNumber: mobile, ServiceBy: 'Mehfil by Smartify' });
    setReused(r?.reused ? r.Date_Time : null);
  };

  // Step 1: the number must be registered — otherwise point them to Sign Up.
  const submitMobile = (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(mobile)) { setErr('Enter a valid 10-digit mobile number'); return; }
    if (isDemoMobile(mobile)) { setErr('The demo account password cannot be changed.'); return; }
    run(async () => {
      const rows = await getUserDetails({ MobileNumber: mobile });
      const u = Array.isArray(rows) ? rows.find((r) => Number(r.Valid) !== 0) : null;
      if (!u) { setErr('This mobile number is not registered. Please sign up.'); return; }
      if (Number(u.IsActive) === 0) { setErr(humanReason('inactive')); return; }
      await requestOtp();
      setOtpRaw('');
      setStep('otp');
    }, 'Could not send the OTP. Check your connection and try again.');
  };

  const resend = () => run(requestOtp, 'Could not resend the OTP');

  // Step 2: check the code.
  const submitOtp = (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(otp)) { setErr('Enter the 4-digit OTP'); return; }
    run(async () => {
      const res = await verifyOtp({ MobileNumber: mobile, OTP: otp });
      if (!res?.verified) { setErr(res?.reason || 'Invalid OTP'); return; }
      setStep('password');
    }, 'Verification failed. Check your connection and try again.');
  };

  // Step 3: save the new password, then sign in here.
  const submitPassword = (e) => {
    e.preventDefault();
    const problem = passwordProblem(pw, mobile);
    if (problem) { setErr(problem); return; }
    if (pw !== confirm) { setErr('Passwords do not match'); return; }
    run(async () => {
      const r = await ResetPasswordWithOtp({ MobileNumber: mobile, Password: pw });
      if (!r?.ok) {
        if (r?.reason === 'otp_required') { setErr('The OTP has expired. Please verify again.'); setStep('otp'); return; }
        setErr({
          not_found: 'This mobile number is not registered.',
          inactive: humanReason('inactive'),
          demo: 'The demo account password cannot be changed.',
          bad_password: 'Password must be 4 to 20 characters',
        }[r?.reason] || 'Could not reset the password.');
        return;
      }
      const res = await verifyDevice(mobile, pw, { bind: true });
      let user = res?.user;
      if (!res?.ok) {
        // Bound to another device: the OTP just verified lets us move it here.
        if (res?.reason !== 'device_mismatch') { setErr(humanReason(res?.reason)); return; }
        user = await bindThisDevice(res.user);
      }
      const list = await loadOrganisers(user);
      if (list.length === 0) {
        nav('/', { replace: true, state: { msg: 'Password changed, but no organiser is assigned to this account yet.' } });
        return;
      }
      login(user, list[0], { mobile, password: pw }, true, false);
      startSessionLog(user, await thisDeviceId());
      nav('/app', { replace: true });
    }, 'Could not reset the password. Check your connection and try again.');
  };

  const back = () => {
    setErr('');
    if (step === 'otp') setStep('mobile');
    else if (step === 'password') setStep('otp');
    else nav('/', { replace: true });
  };
  const toLogin = () => nav('/', { replace: true });

  return {
    step, mobile, setMobile, otp, setOtp, pw, setPw, confirm, setConfirm, show, setShow,
    busy, err, reused, submitMobile, submitOtp, submitPassword, resend, back, toLogin,
  };
}
